import dayjs from "dayjs";

import converteDataUS from "../../../../utils/converteDataUS";

const ENDPOINT_ADICIONA_LOTE = "/v2/faturamento/adicionaLote";
const FALLBACK_ERRO_CRIACAO_LOTE = "Não foi possível criar o lote. Tente novamente.";
const TIPOS_LOTE_VALIDOS = ["NORMAL", "INTERNO", "PENDENCIA", "NEGOCIACAO"];

export const estaVazio = (valor) =>
  valor === null ||
  valor === undefined ||
  (typeof valor === "string" && valor.trim() === "") ||
  (Array.isArray(valor) && valor.length === 0);

export const paraNuloSeVazio = (valor) => (estaVazio(valor) ? null : valor);

export const separarCompetencia = (competencia) => {
  const valor = typeof competencia === "string" ? competencia.trim() : "";
  const resultado = /^(\d{2})\/(\d{4})$/.exec(valor);

  if (!resultado) {
    return null;
  }

  const mes = Number(resultado[1]);

  if (mes < 1 || mes > 12) {
    return null;
  }

  return {
    mes: resultado[1],
    ano: resultado[2],
  };
};

export const validarCompetencia = (competencia) =>
  separarCompetencia(competencia)
    ? null
    : "A competência deve estar no formato MM/AAAA.";

export const validarCamposObrigatoriosLote = ({
  convenio,
  lote,
  dataInicial,
  dataFinal,
  competencia,
  tipo = "NORMAL",
}) => {
  if (estaVazio(convenio)) {
    return "Selecione um convênio.";
  }

  if (estaVazio(lote)) {
    return "Informe o lote.";
  }

  if (estaVazio(dataInicial)) {
    return "Informe a data inicial.";
  }

  if (estaVazio(dataFinal)) {
    return "Informe a data final.";
  }

  if (!dayjs(dataInicial).isValid()) {
    return "Informe uma data inicial válida.";
  }

  if (!dayjs(dataFinal).isValid()) {
    return "Informe uma data final válida.";
  }

  if (dayjs(dataInicial).isAfter(dayjs(dataFinal), "day")) {
    return "A data inicial não pode ser posterior à data final.";
  }

  const erroCompetencia = validarCompetencia(competencia);
  if (erroCompetencia) return erroCompetencia;

  if (!TIPOS_LOTE_VALIDOS.includes(String(tipo).trim().toUpperCase())) {
    return "Selecione um tipo de lote válido.";
  }

  return null;
};

const formatarDataOpcional = (data) => {
  if (estaVazio(data)) {
    return null;
  }

  return converteDataUS(data);
};

const obterCompetencia = ({ competencia, mesCompetencia, anoCompetencia }) => {
  const competenciaSeparada = separarCompetencia(competencia);

  if (competenciaSeparada) {
    return competenciaSeparada;
  }

  return {
    mes: paraNuloSeVazio(mesCompetencia),
    ano: paraNuloSeVazio(anoCompetencia),
  };
};

const formatarCompetenciaComposta = ({ mes, ano }) => {
  if (estaVazio(mes) || estaVazio(ano)) {
    return null;
  }

  return `${String(mes).trim().padStart(2, "0")}/${String(ano).trim()}`;
};

export const montarPayloadProtocolo = ({
  codigo,
  plano,
  protocolo,
  competencia,
  mesCompetencia,
  anoCompetencia,
  previsao,
}) => {
  const competenciaSeparada = obterCompetencia({
    competencia,
    mesCompetencia,
    anoCompetencia,
  });

  return {
    protocolo: paraNuloSeVazio(protocolo),
    previsao: formatarDataOpcional(previsao),
    codigo: paraNuloSeVazio(codigo),
    plano: paraNuloSeVazio(plano),
    competencia: formatarCompetenciaComposta(competenciaSeparada),
    mes_competencia: competenciaSeparada.mes,
    ano_competencia: competenciaSeparada.ano,
  };
};

export const montarPayloadLote = ({
  dataEnvioLote = new Date(),
  dataInicial,
  dataFinal,
  competencia,
  mesCompetencia,
  anoCompetencia,
  codigo,
  plano,
  protocolo,
  previsao,
  valor,
  valorAlterado,
  quantidade,
  emConstrucao,
  usuarioResponsavel,
  tipo = "NORMAL",
}) => {
  const protocoloNormalizado = paraNuloSeVazio(protocolo);
  const competenciaSeparada = obterCompetencia({ competencia, mesCompetencia, anoCompetencia });

  return {
    data_inicial: formatarDataOpcional(dataInicial),
    data_final: formatarDataOpcional(dataFinal),
    data_envio_lote: dataEnvioLote,
    codigo: paraNuloSeVazio(codigo),
    plano: paraNuloSeVazio(plano),
    protocolo: protocoloNormalizado,
    competencia: formatarCompetenciaComposta(competenciaSeparada),
    mes_competencia: competenciaSeparada.mes,
    ano_competencia: competenciaSeparada.ano,
    data_previsao: formatarDataOpcional(previsao),
    valor: paraNuloSeVazio(valor),
    valor_alterado: paraNuloSeVazio(valorAlterado),
    quantidade: paraNuloSeVazio(quantidade),
    em_construcao: emConstrucao,
    tipo: String(tipo || "NORMAL").trim().toUpperCase(),
    usuario_responsavel: paraNuloSeVazio(usuarioResponsavel),
  };
};

export const salvarProtocoloELote = async ({
  payloadLote,
  apiClient,
}) => {
  return apiClient.post(ENDPOINT_ADICIONA_LOTE, payloadLote);
};

const formatarDetalhesErro = detalhes => {
  if (!Array.isArray(detalhes)) {
    return "";
  }

  return detalhes
    .filter(detalhe => typeof detalhe?.mensagem === "string" && detalhe.mensagem.trim())
    .map(detalhe =>
      detalhe.campo ? `${detalhe.campo}: ${detalhe.mensagem.trim()}` : detalhe.mensagem.trim()
    )
    .join(" ");
};

export const extrairMensagemErroCriacaoLote = (
  error,
  fallback = FALLBACK_ERRO_CRIACAO_LOTE
) => {
  const data = error?.response?.data;
  const mensagem = typeof data?.mensagem === "string" ? data.mensagem.trim() : "";
  const detalhes = formatarDetalhesErro(data?.detalhes);
  const idCorrelacao =
    typeof data?.id_correlacao === "string" ? data.id_correlacao.trim() : "";

  if (mensagem) {
    const mensagemComDetalhes = detalhes && !mensagem.includes(detalhes)
      ? `${mensagem} ${detalhes}`
      : mensagem;
    return idCorrelacao
      ? `${mensagemComDetalhes} Código de rastreio: ${idCorrelacao}.`
      : mensagemComDetalhes;
  }

  if (detalhes) {
    return idCorrelacao
      ? `${detalhes} Código de rastreio: ${idCorrelacao}.`
      : detalhes;
  }

  return fallback;
};
